import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { EmpresaCadastralResponse } from '@interfaces/corporate-registry/responses/empresa-cadastral.response';
import { CommercialClientTermResponse } from '@interfaces/partners/responses/commercial-client-term.response';
import { SharedModule } from '@src/app/shared/shared.module';
import { RegistryCompanyCardComponent } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/registry/registry-company-card/registry-company-card.component';
import { RegistryPartnersCardComponent } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/registry/registry-partners-card/registry-partners-card.component';
import { DealConsentCardComponent } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-consent-card/deal-consent-card.component';
import { signedClientTerm } from '@src/app/features/dashboard/commercial/pages/deal-detail/deal-tabs';
import { ClientAccessCardComponent } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/client-access-card/client-access-card.component';

/**
 * Aba **KYC** do deal (card 3380): o que já se sabe sobre o cliente, num lugar só, para o time interno.
 *
 * | Seção | Fonte |
 * | --- | --- |
 * | Consentimento SCR (coluna lateral, card 3418) | termo NDA + carta de autorização de SCR (`partners/deals/{id}/term`), só quando **assinado**; o mesmo `app-deal-consent-card` da Visão geral |
 * | Cadastro · Receita Federal | `corporate-registry/empresas/{cnpj}` (o mesmo cartão da etapa Registro) |
 * | Acionistas / quadro societário | o QSA da mesma consulta (o mesmo cartão de Sócios da etapa Registro) |
 *
 * Os dados chegam prontos do `DealDetailComponent`, que já os carrega para decidir se a aba aparece
 * (`canSeeKyc` em `deal-tabs.ts`): seção sem dado não aparece. A descrição da empresa gerada pelo Cog
 * entra aqui na task #3382 (Victor).
 */
@Component({
  selector: 'app-deal-kyc-tab',
  standalone: true,
  imports: [
    SharedModule,
    RegistryCompanyCardComponent,
    RegistryPartnersCardComponent,
    DealConsentCardComponent,
    ClientAccessCardComponent,
  ],
  templateUrl: './deal-kyc-tab.component.html',
  styleUrl: './deal-kyc-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealKycTabComponent {
  /** Cadastro da empresa na Receita; `null` quando a consulta não trouxe nada. */
  readonly company = input<EmpresaCadastralResponse | null>(null);

  /** Termo do cliente do deal; só o assinado aparece. */
  readonly term = input<CommercialClientTermResponse | null>(null);

  /** O documento do termo está sendo aberto (o botão mostra o carregamento). */
  readonly openingTerm = input(false);

  /** Pede ao pai para abrir o PDF do termo no visualizador. */
  readonly openTerm = output<void>();

  /** Id do deal, para o card "Acesso do cliente" (card 3461). */
  readonly dealId = input<string | null>(null);

  /** Razão social da empresa do deal, para o título da modal de convite. */
  readonly companyName = input<string | null>(null);

  /**
   * `COMMERCIAL_INVITE_CLIENT` (card 3461): o card "Acesso do cliente" entra na coluna lateral.
   * Quem calcula é o pai, que já carrega as permissões da tela.
   */
  readonly canInviteClient = input(false);

  /** Deal recusado: o card de acesso do cliente não oferece convite. */
  readonly dealRejected = input(false);

  /** Repassa ao pai o convite feito no card, para recarregar a linha do tempo. */
  readonly clientInvited = output<void>();

  protected readonly signedTerm = computed(() => signedClientTerm(this.term()));

  protected readonly partners = computed(() => this.company()?.socios ?? []);
}
